import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { Loader2, MapPin, CalendarClock, ChevronRight, Users } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
}
function pin() {
  return L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 13],
    html: `<div class="rp-map-pin" style="background:#C8000F;width:14px;height:14px"></div>` });
}
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) { map.setView(points[0], 7); return; }
    if (points.length > 1) { map.fitBounds(points, { padding: [40, 40], maxZoom: 8 }); }
  }, [points, map]);
  return null;
}

export default function FanSchedule() {
  const m = useIsMobile();
  const nav = useNavigate();
  const [upcoming, setUpcoming] = useState(null);

  useEffect(() => {
    api.get("/competitors/following-upcoming").then((r) => setUpcoming(r.data?.upcoming || [])).catch(() => setUpcoming([]));
  }, []);

  const rodeos = useMemo(() => {
    const byId = new Map();
    for (const x of upcoming || []) {
      if (!byId.has(x.rodeo_id)) {
        byId.set(x.rodeo_id, { id: x.rodeo_id, name: x.rodeo_name, city: x.city, state: x.state, lat: x.lat, lng: x.lng, runs: [] });
      }
      byId.get(x.rodeo_id).runs.push(x);
    }
    const arr = [...byId.values()];
    for (const r of arr) r.runs.sort((a, b) => (a.performance_date || "9999").localeCompare(b.performance_date || "9999"));
    arr.sort((a, b) => (a.runs[0]?.performance_date || "9999").localeCompare(b.runs[0]?.performance_date || "9999"));
    return arr;
  }, [upcoming]);

  const points = useMemo(() => rodeos.filter((r) => typeof r.lat === "number" && typeof r.lng === "number").map((r) => [r.lat, r.lng]), [rodeos]);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const loading = upcoming === null;

  return (
    <div className={page} data-testid="fan-schedule">
      <div className={`px-4 sm:px-6 py-5 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>Your riders' schedule</h1>
        <p className={`text-sm mt-1 ${sub}`}>Every rodeo your followed riders are drawn to — on the map and in order.</p>
      </div>

      <div className="p-4 sm:p-6 max-w-5xl">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : rodeos.length === 0 ? (
          <div className={`rounded-xl border p-6 text-center ${card}`} data-testid="fan-schedule-empty">
            <CalendarClock className="w-8 h-8 text-[#C8000F] mx-auto mb-3" />
            <div className={`font-display font-bold text-lg ${title}`}>No upcoming runs yet</div>
            <p className={`text-sm mt-1 mb-4 ${sub}`}>Follow a few riders and their rodeos will show up here.</p>
            <button onClick={() => nav("/riders")} data-testid="fan-schedule-find"
              className="inline-flex items-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
              <Users className="w-4 h-4" /> Find riders
            </button>
          </div>
        ) : (
          <>
            {points.length > 0 && (
              <div className={`rounded-xl overflow-hidden border mb-5 ${card}`} data-testid="fan-schedule-map">
                <MapContainer center={points[0]} zoom={6} style={{ height: m ? 260 : 360, width: "100%" }} scrollWheelZoom={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                  <FitBounds points={points} />
                  {rodeos.filter((r) => typeof r.lat === "number").map((r) => (
                    <Marker key={r.id} position={[r.lat, r.lng]} icon={pin()}>
                      <Popup>
                        <div className="font-bold text-[13px]">{r.name}</div>
                        <div className="text-[11px] text-slate-500">{[r.city, r.state].filter(Boolean).join(", ")}</div>
                        <div className="text-[11px] mt-1">{[...new Set(r.runs.map((x) => x.rider))].join(", ")}</div>
                        <button onClick={() => nav(`/rodeo/${r.id}`)} className="text-[11px] font-semibold text-[#C8000F] mt-1">View rodeo →</button>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}

            <div className="space-y-2.5">
              {rodeos.map((r) => {
                const riders = [...new Set(r.runs.map((x) => x.rider))];
                return (
                  <button key={r.id} onClick={() => nav(`/rodeo/${r.id}`)} data-testid={`fan-schedule-rodeo-${r.id}`}
                    className={`w-full text-left rounded-xl border p-3.5 flex items-start gap-3 ${card} ${m ? "active:bg-white/5" : "hover:bg-[#FAFBFC]"}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`font-display font-bold ${title}`}>{r.name}</div>
                      <div className={`text-[11px] flex items-center gap-1 ${sub}`}>
                        <MapPin className="w-3 h-3 shrink-0" />{[r.city, r.state].filter(Boolean).join(", ") || "—"}
                      </div>
                      <div className={`mt-1.5 text-[12px] ${title}`}>
                        {riders.map((name) => {
                          const run = r.runs.find((x) => x.rider === name);
                          return (
                            <span key={name} className="inline-flex items-center gap-1 mr-2 mb-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#C8000F]" />
                              <span className="font-semibold">{name}</span>
                              <span className={sub}>· {run?.event}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {r.runs[0]?.performance_date && <div className="text-[11px] font-semibold text-[#C8000F]">{fmtDate(r.runs[0].performance_date)}</div>}
                      <ChevronRight className="w-4 h-4 text-slate-400 inline mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
